import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import easyFitStore from './Redux/Store/EasyFitStore';
import { StyleSheetManager } from 'styled-components';
import { Provider } from 'react-redux';

import { t } from './Services/LocalizationService';
import { updateNotification } from './Redux/Reducers/NotificationSlice';

import { BrowserRouter } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import { NotificationCloseHandlerType } from './Redux/Models/Notification/NotificationCloseHandlerType';
import { MainViewWorkflow } from './Pages/MainComponents/MainView/MainViewModel';
import LoggingService from './Services/LoggingService';

function appHeight() {
    let vh = window.innerHeight / 100;
    document.querySelector(':root').style.setProperty('--vh', `${vh}px`);
}

window.addEventListener('resize', appHeight);
window.addEventListener('error', (event) => {
    displayUnhandledErrorMessage(event);
});

window.addEventListener('unhandledrejection', (event) => {
    displayUnhandledErrorMessage(event);
});

window.addEventListener('offline', () => {
    displayNetworkErrorMessage();
});

appHeight();

const nonceMeta = document.querySelector('meta[property="csp-nonce"]');
const cspNonce = nonceMeta?.content;

// IMPORTANT: Emotion needs insertionPoint for correct ordering
const insertionPoint = document.querySelector(
    'meta[name="emotion-insertion-point"]'
);

const emotionCache = createCache({
    key: 'mui',
    nonce: cspNonce,
    insertionPoint: insertionPoint || undefined,
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <Provider store={easyFitStore}>
            <BrowserRouter>
                <StyleSheetManager nonce={cspNonce}>
                    <CacheProvider value={emotionCache}>
                        <StyledEngineProvider injectFirst>
                            <App />
                        </StyledEngineProvider>
                    </CacheProvider>
                </StyleSheetManager>
            </BrowserRouter>
        </Provider>
    </React.StrictMode>
);
serviceWorker.unregister();
function displayUnhandledErrorMessage(event) {
    console.error(
        `UNHANDLED PROMISE REJECTION\nType: ${event?.type}\nMessage: ${event?.reason?.message}\nStack: ${event?.reason?.stack}`
    );

    if (
        easyFitStore.getState().notification.maintenanceMessage &&
        easyFitStore.getState().notification.maintenanceMessage.isDisplayed
    ) {
        console.warn(
            `Reason: ${event.reason.statusText}\nStatus: ${event.reason.status}`
        );
    } else {
        const previousUserSelectionAction =
            easyFitStore.getState().notification.alertMessage.userSelections[0]
                .action;

        const isPreviousActionInit =
            previousUserSelectionAction === NotificationCloseHandlerType.Init;

        if (isPreviousActionInit) {
            easyFitStore.dispatch(
                updateNotification({
                    ...easyFitStore.getState().notification,
                    alertMessage: {
                        title: t(
                            'common:Common_Feature:ErrorHandling:TitleText3'
                        ),
                        message: [
                            t('common:Common_Feature:ErrorHandling:Text4'),
                        ],
                        detailMessage: '',
                        userSelections: [
                            {
                                content: t('common:Common_Feature:ok'),
                                action: NotificationCloseHandlerType.Reload,
                            },
                        ],
                        isDisplayed: true,
                    },
                })
            );
        }
    }
}

function displayNetworkErrorMessage() {
    let workflow = window.location.pathname.substring(1);
    const workflowTypes = Object.values(MainViewWorkflow);

    if (!workflowTypes.includes(workflow)) {
        LoggingService.error({
            componentName: 'NetworkLossErrorDialog',
            args: ['Invalid workflow type'],
        });
        workflow = MainViewWorkflow.AppFitting;
    }

    easyFitStore.dispatch(
        updateNotification({
            ...easyFitStore.getState().notification,
            alertMessage: {
                title: t(
                    'common:Common_Feature:ErrorHandling:NetworkConnectionLoss:Title'
                ),
                message: [
                    t(
                        'common:Common_Feature:ErrorHandling:NetworkConnectionLoss:Text1'
                    ),
                    t(
                        'common:Common_Feature:ErrorHandling:NetworkConnectionLoss:Text2'
                    ),
                ],
                detailMessage: t(
                    'common:Common_Feature:ErrorHandling:NetworkConnectionLoss:Text3'
                ),
                userSelections: [
                    {
                        content: t('common:Common_Feature:ok'),
                        action: NotificationCloseHandlerType.NetworkLossExit,
                    },
                ],
                workflow: workflow,
                isDisplayed: true,
            },
        })
    );
}
